import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import map from 'lodash/map';
import reject from 'lodash/reject';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { SegmentEventTypes } from '../data/tracking';
import Layout from '../layout';
import Analytics from '../utils/Analytics';
import { FullWidthCellRenderer } from './FullWidthCellRenderer.component';
import { GridCustomHeader } from './GridCustomHeader.component';
import { GridLoadingOverlay } from './GridLoadingOverlay.component';
import { GridNoDataOverlay } from './GridNoDataOverlay.component';
import { Pagination } from './Pagination.component';

// extract LayoutContext component from the module
const { LayoutContext } = Layout;
/**
 * The following column events will be handled.
 */
const COLUMN_MOVED_EVENT = 'columnMoved';
const COLUMN_PINNED_EVENT = 'columnPinned';
const COLUMN_RESIZED_EVENT = 'columnResized';
const COLUMN_VISIBLE_EVENT = 'columnVisible';

export class GridPanel extends Component {
	constructor(props) {
		super(props);

		const {
			className = '',
			columnDefs,
			defaultColDef = {},
			domLayout,
			autoGroupColumnDef = {},
			frameworkComponents = {},
			dataqa = 'grid',
			infiniteScroll = false,
			blockLoadThrottleMillis = 300,
			showPagination = true,
			headerHeight = 42,
			rowHeight = 42,
			rowSelection = 'multiple',
			onRowDragEnd,
			onRowDragEnter,
			onCellDoubleClicked,
			groupSelectsChildren = false,
			groupSelectsFiltered = false,
			rowMultiSelectWithClick = false,
			deltaRowDataMode = false,
			getRowNodeId,
			getRowHeight,
			getRowClass,
			fullWidthCellRenderer = 'FullWidthCellRenderer',
			suppressLoadingOverlay = false,
			suppressPropertyNamesCheck = false,
		} = props;

		// exclude 'StatusBar' module so it doesn't get rendered in DOM (1px height)
		const modules = reject(AllModules, {
			moduleName: '@ag-grid-enterprise/status-bar',
		});

		/**
		 * Check if the selection checkbox is used in the grid. If yes,
		 * pass a function instead so that the checkbox is sticked to
		 * the first column only even after moving columns. See
		 * https://www.ag-grid.com/javascript-grid-column-properties/
		 */
		const firstColDef = isArray(columnDefs) ? columnDefs[0] : {};
		const isCheckboxSelectionEnabled = firstColDef.checkboxSelection === true;
		const isHeaderCheckboxSelectionEnabled =
			firstColDef.headerCheckboxSelection === true;

		this.state = {
			className,
			dataqa,
			gridOptions: {
				accentedSort: true, // need to distiguish between sort uppercase and sort lowercase
				autoGroupColumnDef,
				columnDefs: isArray(columnDefs)
					? columnDefs.map(columnDef => ({
							...columnDef,
							checkboxSelection: isCheckboxSelectionEnabled
								? this.enableFirstColumnCheckboxSelection
								: false,
							headerCheckboxSelection: isHeaderCheckboxSelectionEnabled
								? this.enableFirstColumnCheckboxSelection
								: false,
						}))
					: null,
				defaultColDef: {
					enableRowGroup: false,
					enablePivot: false,
					enableValue: false,
					editable: false,
					filter: false,
					resizable: true,
					sortable: true,
					suppressSizeToFit: false,

					// merge defaultColDef object from the caller
					...defaultColDef,

					// defaultColDef shall not override the following properties
					headerComponent: 'GridCustomHeader',
					headerComponentParams: {
						// pass down our custom function
						setColumnState: this.setColumnState,
						// pass down our custom sort function
						onSortChanged: this.onSortChanged,
						// pass down dataqa attribute
						dataqa,
					},
					menuTabs: ['generalMenuTab', 'columnsMenuTab'],
				},
				deltaRowDataMode,
				frameworkComponents: {
					// merge frameworkComponents object from the caller
					...frameworkComponents,
					GridLoadingOverlay,
					GridNoDataOverlay,
					GridCustomHeader,
					FullWidthCellRenderer,
				},
				getRowNodeId,
				getRowHeight,
				getRowClass,
				groupSelectsChildren,
				groupSelectsFiltered,
				headerHeight,
				loadingOverlayComponent: 'GridLoadingOverlay',
				modules, // provide the grid all enterprise modules
				noRowsOverlayComponent: 'GridNoDataOverlay',
				onCellDoubleClicked,
				onRowDragEnd,
				onRowDragEnter,
				rowDragManaged: true,
				rowHeight,
				rowMultiSelectWithClick,
				rowSelection,
				suppressContextMenu: true,
				domLayout,
				suppressDragLeaveHidesColumns: true,
				isRowSelectable: ({ data }) => data.selectable !== false, // determine whether a row is selectable
				isFullWidthCell: ({ data }) =>
					infiniteScroll === true && data.fullWidthRow === true, // determine whether a row should be full-width
				fullWidthCellRenderer,
				postSort: this.postSort,
				suppressLoadingOverlay,
			},
			showPagination,
		};

		if (suppressPropertyNamesCheck) {
			const { gridOptions } = this.state;
			gridOptions.suppressPropertyNamesCheck = true;
		}

		// reference of ag-grid api
		this.api = null;

		// reference of ag-grid column api
		this.columnApi = null;

		this.columnStateBuffer = null;

		// whether the data is fetching in progress (used only when infinite scroll is turned on)
		this.isFetchingNextPageDataInProgress = false;

		this.onViewportChangedThrottled = throttle(
			this.onViewportChanged,
			blockLoadThrottleMillis
		);
		this.onCellKeyDown = this.onCellKeyDown.bind(this);
	}

	componentDidMount() {
		const { subscribeLayoutUpdateEvent } = this.context;

		if (subscribeLayoutUpdateEvent) {
			subscribeLayoutUpdateEvent('GridPanel', this.onLayoutUpdate);
		}
	}

	componentDidUpdate(prevProps) {
		const { rowData, infiniteScroll } = this.props;

		if (infiniteScroll && this.api && rowData !== prevProps.rowData) {
			// update grid data
			this.updateInifinteScrollGridData();
		}
	}

	componentWillUnmount() {
		this.api = null;
		this.columnApi = null;

		const { unsubscribeLayoutUpdateEvent } = this.context;

		if (unsubscribeLayoutUpdateEvent) {
			unsubscribeLayoutUpdateEvent('GridPanel', this.onLayoutUpdate);
		}
	}

	onGridReady = ({ api, columnApi }) => {
		const { onGridReady } = this.props;
		const { dataqa } = this.state;

		// search for ag-grid root DOM element
		const gridEl = api.gridPanel.getGui();

		// store the reference
		this.api = api;
		this.columnApi = columnApi;

		// set dataqa attribute
		gridEl.setAttribute('data-qa', dataqa);
		gridEl.setAttribute('dataqa', dataqa);

		// load the column state and sort state
		this.loadColumnState();

		// let the parent know the grid is loaded, now go ahead and fetch new data (if needed)
		if (onGridReady) {
			onGridReady({ api, columnApi, sortModel: api.getSortModel() });
		}
	};

	onCellKeyDown = e => {
		if (!e.event) {
			return;
		}
		const keyboardEvent = e.event;
		const { key } = keyboardEvent;

		if (key.length) {
			const selectedNodes = this.api.getSelectedNodes();
			const { rowData } = this.props;

			if (
				selectedNodes &&
				selectedNodes.length > 0 &&
				rowData &&
				selectedNodes.length !== rowData.length
			) {
				const selectedNode = selectedNodes[0];

				this.api.forEachNode(node => {
					if (
						key === 'ArrowDown' &&
						node.rowIndex + 1 === selectedNode.rowIndex + 1 &&
						this.api.getRowNode(node.rowIndex + 1) !== undefined
					) {
						this.api.deselectAll();
						this.api.getRowNode(node.rowIndex + 1).setSelected(true, false);
					}
					if (
						key === 'ArrowUp' &&
						node.rowIndex - 1 === selectedNode.rowIndex - 1 &&
						this.api.getRowNode(node.rowIndex - 1) !== undefined
					) {
						this.api.deselectAll();
						this.api.getRowNode(node.rowIndex - 1).setSelected(true, false);
					}
				});
			}
		}
	};

	onSelectionChanged = ({ api }) => {
		const { onSelectionChanged } = this.props;

		// get selected nodes
		const selection = api.getSelectedNodes();

		// notify the parent the selection
		if (onSelectionChanged) {
			onSelectionChanged(selection);
		}
	};

	onColumnVisible = ({ column }) => {
		const { onColumnVisible } = this.props;

		// send the column data to parent
		if (onColumnVisible) {
			onColumnVisible(column);
		}

		// trigger column state change
		this.setColumnState();
	};

	/**
	 * Ag-Grid's sort event handler
	 */
	onSortChanged = sortModel => {
		const { infiniteScroll, onSortChanged } = this.props;

		if (infiniteScroll) {
			// reset data for infinite scroll grid
			this.api.setRowData([]);
		}

		// notify the parent about the sort change to fetch the new data
		if (onSortChanged) {
			onSortChanged(sortModel);
		}
	};

	onPaginationChange = (currentPage, pageSize) => {
		const sortModel = this.api ? this.api.getSortModel() : null;
		const { fetchData } = this.props;

		// notify the parent to fetch new data (if needed)
		if (fetchData) {
			fetchData(currentPage, pageSize, sortModel);
		}
	};

	onLayoutUpdate = () => {
		if (this.api) {
			// make grid 100% wide
			this.api.sizeColumnsToFit();
		}
	};

	onRowClicked = ({ node }) => {
		// toggle row expanded/collapsed state
		node.setExpanded(!node.expanded);
	};

	onRowSelect = ({ node }) => {
		const { dataqa } = this.props;

		// Track Segment event whenever a selection is made
		Analytics.trackSegmentEvent({
			trackingData: {
				field_type: 'selection',
				field_name_dataqa: `${dataqa}-grid-selection`,
			},
			eventType: SegmentEventTypes.UserInteraction,
		});
	};

	onColumnEvent = ({ type, source, finished }) => {
		if (type === COLUMN_VISIBLE_EVENT) {
			// when user explicitly shows or hides the column through the column menu
			if (source === 'columnMenu') {
				this.setColumnState();
			}
		} else if (type === COLUMN_PINNED_EVENT) {
			// when user explicitly pins or un-pins the column through the column menu
			if (source === 'contextMenu') {
				this.setColumnState();
			}
		} else if (type === COLUMN_MOVED_EVENT) {
			// when user explicitly moves the column
			if (source === 'uiColumnDragged') {
				this.setColumnState();
			}
		} else if (type === COLUMN_RESIZED_EVENT) {
			// when user explicitly finishes resizing the column width
			if (source === 'uiColumnDragged' && finished === true) {
				this.setColumnState();
			}
		}
	};

	/**
	 * Ag-Grid's ViewportChange event handler
	 * @param {object} params
	 * - firstRow {number} the index of first row
	 * - lastRow {number} the index of last row
	 */
	onViewportChanged = ({ api, firstRow, lastRow }) => {
		const { nextPage, fetchData, rowBuffer = 20 } = this.props;
		const displayedRowCount = api.getDisplayedRowCount();
		const remainingRows = displayedRowCount - (lastRow + 1);

		/**
		 * Dev note:
		 * The following conditions are used to avoid overly calling 'fetchData' method:
		 *
		 * (1) total rows should be greater than 0
		 * (2) first row index should not be 0, and last row index should not be -1
		 * (3) remaining rows should be within the row buffer
		 * (4) `isFetchingNextPageDataInProgress` flag should be false
		 * (5) nextPage equal to true indicating more data to fetch
		 */
		if (
			displayedRowCount > 0 &&
			firstRow > 0 &&
			lastRow !== -1 &&
			remainingRows <= rowBuffer &&
			this.isFetchingNextPageDataInProgress === false &&
			nextPage === true
		) {
			this.isFetchingNextPageDataInProgress = true;
			fetchData();
		}
	};

	// notify the owner with the changed column state if the handler is provided
	setColumnState = () => {
		if (this.columnStateBuffer) {
			clearTimeout(this.columnStateBuffer);
			this.columnStateBuffer = null;
		}

		// please see 'Interface / API > Column Interface > Column Object' for details why
		// setTimeout is used here (https://www.ag-grid.com/javascript-grid-column/).
		this.columnStateBuffer = setTimeout(() => {
			const { onColumnStateChanged } = this.props;

			// caller needs to pass the handler to handle the columns state change
			if (onColumnStateChanged) {
				// gets the state of the columns
				const columnState = this.buildColumnState();

				// notify the owner the column state
				onColumnStateChanged(columnState);
			}
		}, 500);
	};

	// load the column state and sort state to the grid instance
	loadColumnState = () => {
		const { columnState } = this.props;
		const sortModel = [];

		// WEBAPPS-27465, loading empty columnState array will cause grid to render nothing
		if (isArray(columnState) && columnState.length > 0) {
			// parse sort state
			forEach(columnState, ({ colId, sort }) => {
				if (isString(sort) && sort !== '') {
					sortModel.push({ colId, sort });
				}
			});

			/**
			 * To restore the grid with the stored column state object. Ag-grid will
			 * perform its best to apply what's applicable in the state object on
			 * the grid. If one or more columns state no longer exist in the grid's
			 * columns definition, they will simply be ignored and not applied.
			 */
			this.columnApi.setColumnState(columnState);

			// restore the grid with the stored sort state
			if (sortModel.length === 1) {
				this.api.setSortModel(sortModel);
			}
		}
	};

	// build the column state object.
	buildColumnState = () => {
		const columnState = this.columnApi.getColumnState();
		const sortModel = this.api.getSortModel();
		const sortedColumn = sortModel.length === 1 ? sortModel[0] : null;

		if (sortedColumn) {
			return map(columnState, column => ({
				...column,

				// including sort value in the state
				sort: column.colId === sortedColumn.colId ? sortedColumn.sort : null,
			}));
		} else {
			return columnState;
		}
	};

	// return `true` to render the selection checkbox
	enableFirstColumnCheckboxSelection = ({ columnApi, column }) => {
		// get all displayed columns
		const displayedColumns = columnApi.getAllDisplayedColumns();

		// enable the selection checkbox for the first column
		return displayedColumns[0] === column;
	};

	/**
	 * Return an array of full-width row nodes.
	 * Full-width row is used for spinner and end-data message.
	 *
	 * @returns {array}
	 */
	getFullWidthRows = () => {
		const fullWidthRows = [];

		this.api.forEachNode(({ data }) => {
			if (data.fullWidthRow === true) {
				fullWidthRows.push(data);
			}
		});

		return fullWidthRows;
	};

	/**
	 * Dev note:
	 * For using the infinite scroll feature, we do NOT use setState to provide data to Ag-grid as
	 * it will cause the grid view to re-render, resulting ui to flicker.
	 *
	 * Update Grid data using the grid's api. This is only called when grid's infinite scroll
	 * feature is turned on.
	 */
	updateInifinteScrollGridData = () => {
		const {
			rowData,
			pageSize = 100,
			endOfDataMessage,
			showEndOfDataMessage,
		} = this.props;

		const spinnerOrMessageRow = {
			/**
			 * Dev note:
			 * Copy the first data object so that this full-width rowNode has the
			 * same data structure as the rest rowNodes.
			 *
			 * This is crucial especially when data contains nested data structure,
			 * and `valueGetter` is defined for the column(s) in `columnDefs`.
			 * See https://stamps.atlassian.net/browse/WEBAPPS-33050.
			 */
			...rowData[0],

			// true to render a full-width row
			fullWidthRow: true,

			// false to set this row unselectable
			selectable: false,

			// true to show spinner
			showSpinner: rowData && rowData.length === pageSize,

			// message to display when grid has no more data to load
			endOfDataMessage: endOfDataMessage || 'No more data.',
		};

		// get full-width row nodes
		const fullWidthRows = this.getFullWidthRows();

		let newRowData = isArray(rowData) ? rowData : [];

		// append a spinner when we don't want an end of data message
		if (rowData && rowData.length === pageSize) {
			newRowData = newRowData.concat(spinnerOrMessageRow);
		} else if (showEndOfDataMessage !== false) {
			// append a spinner and end of data message
			newRowData = newRowData.concat(spinnerOrMessageRow);
		}

		if (this.isFetchingNextPageDataInProgress) {
			this.api.updateRowData({
				// add new data in which it contains new full-width row data
				add: newRowData,

				// remove the previous full-width row data
				remove: fullWidthRows,
			});
		} else {
			// this forces the grid to wash out all existing rows before loading new rows
			this.api.setRowData(newRowData);
		}

		// reset the flag to false
		this.isFetchingNextPageDataInProgress = false;
	};

	// make sure the full-width row appears at the bottom regardless of the sort direction
	postSort = rowNodes => {
		const { infiniteScroll } = this.props;
		let fullWidthRowIndex;

		if (infiniteScroll && isArray(rowNodes) && rowNodes.length > 0) {
			for (let index = 0; index < rowNodes.length - 1; index++) {
				if (rowNodes[index].data?.fullWidthRow === true) {
					// find the index of full-width rowNode
					fullWidthRowIndex = index;
					break;
				}
			}

			/**
			 * When full-width node is found, move it to the bottom of the list.
			 * This is done the same way as the example provided in the documentation.
			 * https://www.ag-grid.com/javascript-data-grid/row-sorting/#post-sort
			 */
			if (isNumber(fullWidthRowIndex)) {
				const [fullWidthRowNode] = rowNodes.splice(fullWidthRowIndex, 1);
				rowNodes.push(fullWidthRowNode);
			}
		}
	};

	/**
	 * The client has updated data for the grid using api.updateRowData.
	 * Notify the parent about the total row count in the grid.
	 */
	onRowDataUpdated = ({ api }) => {
		const {
			infiniteScroll,
			onRowDataUpdated,
			showEndOfDataMessage = true,
		} = this.props;

		if (isFunction(onRowDataUpdated)) {
			let displayedRowCount = api.getDisplayedRowCount();

			/**
			 * Subtrack 1 from the total row count if the infinite-scroll is on because
			 * the last row is not an actual data node but the end-of-data message node.
			 */
			if (infiniteScroll && showEndOfDataMessage) {
				displayedRowCount -= 1;
			}

			onRowDataUpdated(displayedRowCount);
		}
	};

	render() {
		const { className, showPagination, dataqa, gridOptions } = this.state;
		const {
			currentPage,
			loadingOverlayParams,
			noRowsOverlayParams,
			rowData,
			pageSize,
			pageSizeOptions,
			totalPages,
			infiniteScroll,
		} = this.props;

		return (
			<div className={`common-grid ${className}`}>
				<div className="common-grid-wrapper">
					{/* grid */}
					<div className="ag-grid-container ag-theme-common">
						<AgGridReact
							{...gridOptions}
							loadingOverlayComponentParams={{
								loadingMessage: 'Loading ...',
								...loadingOverlayParams,
							}}
							noRowsOverlayComponentParams={noRowsOverlayParams}
							rowData={infiniteScroll ? null : rowData}
							onGridReady={this.onGridReady}
							onSelectionChanged={this.onSelectionChanged}
							onColumnVisible={this.onColumnVisible}
							onColumnResized={this.onColumnEvent}
							onColumnMoved={this.onColumnEvent}
							onColumnPinned={this.onColumnEvent}
							onRowClicked={this.onRowClicked}
							onRowSelected={this.onRowSelect}
							onRowDataChanged={this.onRowDataUpdated}
							onRowDataUpdated={this.onRowDataUpdated}
							onSortChanged={this.onColumnEvent}
							onViewportChanged={this.onViewportChangedThrottled}
							onCellKeyDown={this.onCellKeyDown}
						/>
					</div>

					{/* grid pagination */}
					{showPagination && !infiniteScroll && (
						<Pagination
							currentPage={currentPage}
							totalPages={totalPages}
							pageSize={pageSize}
							dataqa={dataqa}
							pageSizeOptions={pageSizeOptions}
							onPaginationChange={this.onPaginationChange}
						/>
					)}
				</div>
			</div>
		);
	}
}

GridPanel.contextType = LayoutContext;

GridPanel.propTypes = {
	/**
	 * The grid data.
	 *
	 * Dev note:
	 * ag-grid's rowData should be `null` initially so:
	 * 1. grid's loading overlay appears automatically when the component is initially rendered, and
	 * 2. grid's no-data overlay will not show up initially.
	 * (See WEBAPPS-27227)
	 */
	rowData: PropTypes.any,

	// The grid columns configuation.
	columnDefs: PropTypes.array.isRequired,

	// (Optional) The state of grid columns stored in user preferences.
	columnState: PropTypes.array,

	// (Optional) Additional ClassName of the component
	className: PropTypes.string,

	// (Optional) To fetch new data.
	fetchData: PropTypes.func,

	// (Optional) Object that contains column properties all columns will inherit.
	defaultColDef: PropTypes.object,

	// (Optional) 'autoHeight' to allow the grid to auto-size its height to the number of the rows in the grid.
	// See https://www.ag-grid.com/javascript-grid-width-and-height/
	domLayout: PropTypes.string,

	// (Optional) Object that contains parameters to GridLoadingOverlay component.
	loadingOverlayParams: PropTypes.any,

	// (Optional) Object that contains parameters to GridNoDataOverlay component.
	noRowsOverlayParams: PropTypes.any,

	// (Optional) Page size options. Default options are: [100, 250, 500].
	pageSizeOptions: PropTypes.array,

	// (Optional) Number of rows to display per page. Default to 100.
	pageSize: PropTypes.number,

	// (Optional) Flag to show/hide pagination toolbar.
	showPagination: PropTypes.bool,

	// (Optional) Current viewing page. Default to 1.
	currentPage: PropTypes.number,

	// (Optional) Total pages to display. Default to 1.
	totalPages: PropTypes.number,

	// (Optional) The dataqa attribute for QA's testing.
	dataqa: PropTypes.string,

	// (Optional) To get ag-grid API.
	onGridReady: PropTypes.func,

	// (Optional) To get grid selection.
	onSelectionChanged: PropTypes.func,

	// (Optional) Header Height and Row Height. Default to 42.
	// Note: per ag-grid documentation, if you are going to change the row or header height
	// globally, you should also modify the these SASS variables, $ag-grid-row-height and
	// $ag-grid-header-height in the theme. And if you just need the different height for
	// your component, you will need to add local CSS style rules.
	// See https://www.ag-grid.com/javascript-grid-themes-provided/.
	headerHeight: PropTypes.number,
	rowHeight: PropTypes.number,

	// (Optional) To store grid's column state in the database.
	onColumnStateChanged: PropTypes.func,

	// (Optional) Specfies your own configuration used by the auto group columns.
	autoGroupColumnDef: PropTypes.object,

	// (Optional) When true, if you select a group, its children will also be selected.
	groupSelectsChildren: PropTypes.bool,

	// (Optional) If using groupSelectsChildren, the only the children that pass teh current filter will get selected.
	groupSelectsFiltered: PropTypes.bool,

	// (Optional) When true, multiple rows can be selected with clicks without the need for ctrl or shift.
	rowMultiSelectWithClick: PropTypes.bool,

	// (Optional) Compares the current row data to the new data to see the delta and update accordingly
	deltaRowDataMode: PropTypes.bool,

	// (Optional) Returns the row node with the given ID.
	// Used in conjunction with deltaRowDataMode to provide a unique identifier to calculate the delta
	getRowNodeId: PropTypes.func,

	// (Optional) Used to change the row height so that each row can have a different height
	getRowHeight: PropTypes.func,

	// (Optional) Used to add classes to rows so that each row can have it's own styling
	getRowClass: PropTypes.func,

	/**
	 * Infinite Scroll feature
	 * See https://auctane.atlassian.net/wiki/spaces/RnD/pages/3586701412/History+-+Infinite+Scroll
	 */

	// (Optional) Set `true` to turn on the infinite scroll feature. Default to false.
	infiniteScroll: PropTypes.bool,

	// (Optional) Throttling restricts the grid to check when to load the next block of rows once every X milliseconds.
	// Default to 300 milliseconds.
	blockLoadThrottleMillis: PropTypes.number,

	// (Optional) The number of rows that have yet rendered in the viewable area. Default to 20.
	rowBuffer: PropTypes.number,

	// (Optional) The message to display when grid has no more rows to load. Default to 'No more data.'
	endOfDataMessage: PropTypes.string,

	// (Optional) When `true`, it will try fetching next page data when user scrolls to the bottom
	nextPage: PropTypes.bool,

	// (Optional) The parent component to know the total number of rows there are in the grid
	onRowDataUpdated: PropTypes.func,
};
